<template>
  <div>
    <div class="row">
      <div class="columns">
        <p class="trial-subscription-settings__title">
          {{ $t('APP.IAB.TRIAL_SUBSCRIPTION_TITLE') }}
        </p>

        <p
          v-dompurify-html="description"
          class="trial-subscription-settings__description"
        />
      </div>

      <div class="columns">
        <Card v-if="isActivated" v-loading="loading">
          <div class="row align-middle">
            <div class="columns">
              <ElButton
                type="outlined"
                :loading="loading"
                @click="openDeactivateConfirm"
              >
                {{ $t('APP.IAB.DEATIVATE_TRIAL_SUBSCRIPTION') }}
              </ElButton>
            </div>

            <div class="columns shrink">
              <span class="trial-subscription-settings__prop">
                {{ $t('APP.IAB.TRIAL_SUBSCRIPTION_PERIOD') }}:
              </span>

              <b class="trial-subscription-settings__value">
                {{ model.period }}
                {{ $t('DAY') }}
              </b>
            </div>
          </div>
        </Card>

        <Card v-else v-loading="loading">
          <ElForm
            v-if="showActivateForm"
            ref="trialForm"
            label-position="right"
            :model="model"
            :rules="rules"
            inline
            class="trial-subscription-settings__form"
            @submit.native.prevent="activate"
          >
            <ElFormItem
              prop="period"
              class="trial-subscription-settings__input"
            >
              <ElInput
                v-model="model.period"
                name="period"
                type="number"
                min="1"
                max="30"
                class="el-input-hide-numeric-counter"
                :placeholder="$t('APP.IAB.TRIAL_PERIOD_IN_DAYS')"
              />
            </ElFormItem>

            <ElFormItem>
              <ElButton type="primary" native-type="submit" :loading="loading">
                {{ $t('APP.IAB.ACTIVATE_BTN') }}
              </ElButton>
            </ElFormItem>
          </ElForm>

          <ElButton
            v-else
            type="primary"
            :loading="loading"
            @click="showActivateForm = true"
          >
            {{ $t('APP.IAB.ACTIVATE_TRIAL_SUBSCRIPTION') }}
          </ElButton>
        </Card>
      </div>
    </div>
    <hr class="divider" />
  </div>
</template>

<script>
import Card from '@@/common/Card';
import { TrialSubscription } from '@/api/api';
import form from '@/mixins/form';
import lang from '@/mixins/lang';
import packageNameWatch from '@/mixins/apps/packageNameWatch';

export default {
  name: 'TrialSubscriptionSettings',

  components: {
    Card,
  },

  mixins: [form, packageNameWatch, lang],

  props: {
    packageName: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      loading: false,
      isActivated: false,
      showActivateForm: false,

      model: {
        period: '',
      },
    };
  },

  computed: {
    rules() {
      return {
        period: [
          {
            type: 'number',
            validator: (rule, value, cb) => {
              const num = Number(value);
              if (isNaN(num) || num > 30 || num < 0) {
                return cb(
                  new Error(this.$t('APP.IAB.TRIAL_SUBSCRIPTION_PERIOD_ERROR')),
                );
              }

              return cb();
            },
          },
          {
            required: true,
            message: this.$t('VALIDATION_REQUIRED_ERR'),
          },
        ],
      };
    },

    description() {
      const lang = this.lang === 'fa' ? 'fa' : 'en';
      return this.$t('APP.IAB.TRIAL_SUBSCRIPTION_DESCRIPTION', {
        link: `<a class="link" target="_blank" data-s="2" href="https://developers.cafebazaar.ir/${lang}/guidelines/in-app-billing/subscription#Trial-subscription" target="_blank">
        ${this.$t('DOCUMENTATION')}
        </a>`,
      });
    },
  },

  mounted() {
    this.getInfo();
  },

  methods: {
    getInfo() {
      this.loading = true;

      TrialSubscription.getPeriod({
        packageName: this.packageName,
      })
        .then(({ body: { trial_period = 0 } }) => {
          this.isActivated = true;
          this.model.period = trial_period;
        })
        .finally(() => (this.loading = false));
    },

    activate() {
      if (!this.isValid('trialForm')) {
        return false;
      }

      this.loading = true;

      return TrialSubscription.changePeriod(
        { packageName: this.packageName },
        {
          trialPeriod: this.model.period,
        },
      )
        .then(({ body: { message } = {} }) => {
          if (message) {
            this.$message.success(message);
          }
          this.isActivated = true;
          this.showActivateForm = false;
          this.$emit('update');
        })
        .catch(({ body: { message } = {} }) => {
          if (message) {
            this.$message.error(message);
          }
        })
        .finally(() => (this.loading = false));
    },

    openDeactivateConfirm() {
      return this.$confirm(
        this.$t('APP.IAB.DEATIVATE_TRIAL_SUBSCRIPTION_CONFIRM'),
        this.$t('APP.IAB.DEATIVATE_TRIAL_SUBSCRIPTION'),
        {
          confirmButtonText: this.$t('DISABLE'),
          cancelButtonText: this.$t('CANCEL'),
          cancelButtonClass: 'el-button--primary',
          confirmButtonClass: 'el-button--text',
          type: 'warning',
        },
      )
        .then(() => {
          return this.deactivate();
        })
        .catch(() => {
          // As there is nothing to do with this,
          // I only want to specify a function to handle
          // an unhandled promise rejection
        });
    },

    deactivate() {
      this.loading = true;

      return TrialSubscription.deactivate({ packageName: this.packageName })
        .then(({ body: { message } = {} }) => {
          if (message) {
            this.$message.success(message);
          }

          this.isActivated = false;
          this.showActivateForm = false;
          this.model.period = '';
          this.$emit('update');
        })
        .catch(({ body: { message } = {} }) => {
          if (message) {
            this.$message.error(message);
          }
        })
        .finally(() => (this.loading = false));
    },
  },
};
</script>

<style lang="scss">
.trial-subscription-settings {
  &__title {
    font-size: rem-calc(25px);
    font-weight: 400;
    margin-bottom: $global-margin;
  }

  &__description {
    font-size: rem-calc(14px);
    font-weight: 400;
    color: $lb-light-black;
  }

  &__form {
    display: flex;
    align-items: flex-start;
  }

  &__input {
    flex-grow: 1;

    .el-form-item__content {
      width: 100%;
    }
  }

  &__prop {
    font-size: rem-calc(14px);
    color: #606266;
  }

  &__value {
    font-size: rem-calc(14px);
  }
}
</style>
